import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowClockwise, Warning, DownloadSimple, UploadSimple, Receipt } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import { dmY } from "@/lib/dateFmt";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

/**
 * v1.29.99 — Invalid UTR Records report.
 * Lists every stored UTR that is NOT exactly 12 numeric digits (contains
 * dots / letters / spaces or wrong length) across slips + manual transactions.
 * Read-only: nothing is auto-corrected or deleted — admin reviews and fixes.
 */
export default function InvalidUtrs() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/invalid-utrs");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openRow = (r) => {
        if (r.kind === "upload") {
            navigate(`/admin/uploads?upload_id=${r.id}&_open=${Date.now()}`);
        } else {
            navigate(`/admin/reports?partyId=${r.party_id}`);
        }
    };

    const exportXlsx = async () => {
        setDownloading(true);
        try {
            const res = await api.get("/reports/invalid-utrs.xlsx", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "invalid-utr-records.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDownloading(false); }
    };

    const rows = data?.rows || [];

    return (
        <div className="space-y-6" data-testid="invalid-utrs-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md">
                            <Warning size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Invalid UTR Records</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Every stored UTR that is not exactly 12 numeric digits (dots, letters, spaces or wrong length). Review only — nothing is auto-changed.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} disabled={loading} data-testid="invalid-utr-refresh"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-100 disabled:opacity-50">
                        <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button onClick={exportXlsx} disabled={downloading || rows.length === 0} data-testid="invalid-utr-export"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                        <DownloadSimple size={14} /> {downloading ? "Preparing…" : "Export to Excel"}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800">Invalid UTR records</div>
                    <div className="mt-2 font-display text-2xl font-bold text-amber-900" data-testid="invalid-utr-count">{data?.count ?? "…"}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Slips</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900">{rows.filter((r) => r.kind === "upload").length}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Transactions</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900">{rows.filter((r) => r.kind === "txn").length}</div>
                </div>
            </div>

            {rows.length === 0 && !loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    <EmptyState icon={Warning} title="No invalid UTRs" description="Every stored UTR is a clean 12-digit number." />
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full text-sm" data-testid="invalid-utr-table">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Date</th>
                                <th className="px-4 py-3 text-left">Party Name</th>
                                <th className="px-4 py-3 text-left">Sub-Party Name</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                                <th className="px-4 py-3 text-left">UTR Value</th>
                                <th className="px-4 py-3 text-left">Uploaded By</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r) => (
                                <tr key={`${r.kind}-${r.id}`} data-testid={`invalid-utr-row-${r.id}`}
                                    onClick={() => openRow(r)}
                                    className="cursor-pointer hover:bg-amber-50/60">
                                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-700">{r.date ? dmY(r.date) : "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${r.kind === "upload" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                                                {r.kind === "upload" ? <UploadSimple size={9} /> : <Receipt size={9} />}
                                                {r.kind === "upload" ? "Slip" : "Txn"}
                                            </span>
                                            <span className="font-medium text-slate-900">{r.party_name}</span>
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">{r.sub_party_name || "—"}</td>
                                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(r.amount)}</td>
                                    <td className="px-4 py-3">
                                        <span className="rounded bg-rose-50 px-2 py-0.5 font-mono text-xs font-semibold text-rose-700 ring-1 ring-rose-200">{r.utr || "—"}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-600">{r.uploaded_by || "System"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
